import { EBankAccountOwnerType, EMangoVopStatus } from "../types/contact.types";
import { getLocalizedText } from "./i18n/i18n.utils";
// The owner name travels to the Mangopay recipient, the Banqup bankwire
// instructions and generated PDFs unchanged, so it has to stay inside the SEPA
// (ISO 20022) basic Latin set: letters, digits, space and / - ? : ( ) . , ' +
// Kept in step with sanitizeOwnerName in rentio-api-next, which has the final
// say — this copy only exists to give the broker immediate feedback.
var SEPA_DISALLOWED_CHARACTERS = /[^A-Za-z0-9/\-?:().,'+ ]/g;
var COMBINING_MARKS = /[\u0300-\u036f]/g;
var REPEATED_WHITESPACE = /\s+/g;
/**
 * NFD splits most accented letters into a base letter plus a combining mark,
 * which leaves the base letter behind once the marks are dropped. These have no
 * decomposition, so without an explicit mapping they would be stripped as
 * disallowed characters and take part of the name with them - a Nordic or
 * German holder name would come out mangled rather than transliterated.
 */
var NON_DECOMPOSING_LATIN = {
    Æ: "AE",
    Ð: "D",
    Ø: "O",
    Œ: "OE",
    Þ: "TH",
    Đ: "D",
    Ł: "L",
    ß: "ss",
    æ: "ae",
    ð: "d",
    ø: "o",
    œ: "oe",
    þ: "th",
    đ: "d",
    ł: "l",
};
var NON_DECOMPOSING_LATIN_PATTERN = new RegExp("[".concat(Object.keys(NON_DECOMPOSING_LATIN).join(""), "]"), "g");
// ISO 20022 caps a Name at 70 characters
export var OWNER_NAME_MAX_LENGTH = 70;
export var getMangoVopStatusInfo = function (status) {
    if (!status) {
        return null;
    }
    switch (status) {
        case EMangoVopStatus.Match:
            return {
                color: "#2e7d32",
                title: getLocalizedText("bank_account.mango_vop_status.match"),
            };
        case EMangoVopStatus.CloseMatch:
            return {
                color: "#ed6c02",
                title: getLocalizedText("bank_account.mango_vop_status.close_match"),
            };
        case EMangoVopStatus.NoMatch:
        case EMangoVopStatus.MatchNotPossible:
            return {
                color: "#d32f2f",
                title: getLocalizedText("bank_account.mango_vop_status.no_match"),
            };
        case EMangoVopStatus.Pending:
            return {
                color: "#6b7280",
                title: getLocalizedText("bank_account.mango_vop_status.pending"),
            };
        default:
            return null;
    }
};
/**
 * Folds diacritics to their base letter, drops everything outside the SEPA
 * basic Latin set and collapses whitespace. Returns an empty string when
 * nothing usable is left.
 */
export var sanitizeOwnerName = function (ownerName) {
    return ownerName
        .replace(NON_DECOMPOSING_LATIN_PATTERN, function (character) { return NON_DECOMPOSING_LATIN[character]; })
        .normalize("NFD")
        .replace(COMBINING_MARKS, "")
        .replace(SEPA_DISALLOWED_CHARACTERS, " ")
        .replace(REPEATED_WHITESPACE, " ")
        .trim()
        .slice(0, OWNER_NAME_MAX_LENGTH)
        .trim();
};
/**
 * Returns the i18n key of the reason the name cannot be used, or undefined when
 * it can. An individual needs two words because Mangopay verifies a natural
 * person against a FirstName/LastName pair; a business name is one field.
 */
export var getOwnerNameError = function (params) {
    var ownerName = params.ownerName, ownerType = params.ownerType;
    var sanitized = sanitizeOwnerName(ownerName);
    if (sanitized.length === 0) {
        return "bank_account.edit_owner_name.error.empty";
    }
    if (ownerType === EBankAccountOwnerType.Individual &&
        sanitized.split(" ").length < 2) {
        return "bank_account.edit_owner_name.error.needs_first_and_last_name";
    }
    return undefined;
};
/**
 * Whether saving the edit would change what is stored. Every save creates a new
 * bank account and a new Mangopay recipient, so an edit that changes nothing must
 * not be saved. The name is compared as it would be stored, sanitized, so a
 * stored name that was never sanitized still counts as a change.
 */
export var hasOwnerNameChanged = function (params) {
    var ownerName = params.ownerName, ownerType = params.ownerType, currentOwnerName = params.currentOwnerName, currentOwnerType = params.currentOwnerType;
    return (sanitizeOwnerName(ownerName) !== currentOwnerName ||
        ownerType !== currentOwnerType);
};
